import React from 'react'
import styled from 'styled-components'
import { Controller } from "react-hook-form"
import { TOOLTIPS } from 'constants/tooltips'

// components
import Grid from '@material-ui/core/Grid'
import FormItem from '../FormItem'
import { BtnPrimary } from '../StyledComponents'
import { H3 } from 'components/typography/H3'

// styled components
const GreenText = styled.span`
  color: ${({theme}) => theme.PRIMARY_COLOR};
  font-weight: bold;
`;

const DetailTitle = styled.div`
  font-weight: bold;
  width: 120px;
  min-width: 120px;
  display: inline-block;
`;

const DetailContainer = styled.div`
  display: flex;
`;

// functions and properties
const sendAnalytics = (data) => {
  fetch(
    `${process.env.REACT_APP_API_URL}/census/jc-analytics`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  )
}

const IndustryRevenueMapping = {
  'Hotel': 'hotel_revenue',
  'Multi-Family': 'apartment_revenue',
  'Condominiums': 'condo_revenue',
  'Gas Station': 'gas_station_revenue',
  'Assisted Living': 'assisted_living_revenue',
  'Restaurant': 'restaurant_revenue',
  'Office Space': 'condo_revenue',
  'Single-Family': 'single_family_revenue',
  'Manufacturing': 'manufacturing_revenue',
};

const Costing = ({ 
  onCalculation, 
  address, 
  tractData, 
  industry,
  form
}) => {

  function submit(data) {
    let { hardCost, duration, architecture, furniture, revenue } = data;
    const {
      costs_inflation,
      revenues_inflation,
      hard_less_24,
      hard_greater_24,
      ff_ec,
      arch_eng,
    } = tractData.multipliers;

    const hratio = [hard_greater_24, hard_less_24];

    let softCost = (architecture * arch_eng * costs_inflation) + (furniture * ff_ec * costs_inflation);
    let revenueRatio = tractData.multipliers[IndustryRevenueMapping[industry.name]]
    hardCost = hardCost * hratio[duration] * costs_inflation;
    revenue = revenue * revenueRatio * revenues_inflation;

    let result = softCost + hardCost + revenue;
    data.jobs = result;

    sendAnalytics({
      address,
      timestamp: new Date(),
      duration: duration === 0 ? '24 months or longer' : 'Less than 24 months',
      hardCost,
      softCost,
      revenue,
      result
    });

    onCalculation(
      data
    );
  }

  return (
    <div>
      <H3 style={{textAlign: 'center'}}>Preliminary EB-5 Job Creation Analysis</H3>
      <br/>
      <Grid container>

        {/* SELECTED INDUSTRY */}
        <Grid container direction="column" style={{width: 'auto', marginRight: 20}}>
          <Grid container direction="column" alignItems="center">
            <GreenText>{industry.name}</GreenText>
            <GreenText>EB-5 Project</GreenText>          
          </Grid>
          <img
            src={require(`assets/images/${industry.img}`).default}
            style={{height: 'auto', width: '100px', margin: '10px 0'}}
          />
        </Grid>

        {/* TRACT DETAILS */}
        <div>
          <Grid container direction="column" justify="space-between" style={{height: '100%', fontSize: 14}}>
            <div><b>Geographic Location Details</b></div>
            <DetailContainer>
              <DetailTitle>Tract Number</DetailTitle>
              <span>{tractData.name}</span>
            </DetailContainer>
            <DetailContainer>
              <DetailTitle>Project MSA</DetailTitle>
              <span>{tractData.msa}</span>
            </DetailContainer>
            <DetailContainer>
              <DetailTitle>County</DetailTitle>
              <span>{tractData.county}</span>
            </DetailContainer>
            <DetailContainer>
              <DetailTitle>City</DetailTitle>
              <span>{tractData.city}</span>
            </DetailContainer>
          </Grid>
        </div>
        
      </Grid>
      <br/>

      {/* FORM */}
      <div>
        <form onSubmit={form.handleSubmit(submit)}>
          <h3>1. Enter Hard Construction Cost</h3>
          <br/>
          <Controller 
            name="hardCost"
            control={form.control}
            rules={{ required: true }}
            render={props => (
              <FormItem
                title={'Hard Cost'}
                type="number"
                placeholder={'Enter cost ($ in millions)'}
                tooltip = { TOOLTIPS.hardCost }
                thousandSeparator={true}
                background={'rgb(0, 112, 192)'}
                name="hardCost"
                errorMsg={form.errors.hardCost && 'required'}
                {...props}
              />
            )}
          />
          <Controller 
            name="duration"
            control={form.control}
            rules={{ required: true }}
            render={props => (
              <FormItem
                title={'Duration'}
                placeholder={'Select duration (more/less than 24 months)'}
                tooltip = { TOOLTIPS.duration }
                select={true}
                options={[{name: 'Less than 24 months', value: '1'}, {name: '24 months or longer', value: '0'}]}
                background={'rgb(0, 112, 192)'}
                name="duration"
                errorMsg={form.errors.duration && 'required'}
                {...props}
              />
            )}
          />
          <br/>
          <h3>2. Enter Soft Construction Costs</h3>
          <br/>
          <Controller 
            name="architecture"
            control={form.control}
            rules={{ required: true }}
            render={props => (
              <FormItem
                title={'Architecture and Engineering Fees'}
                placeholder={'Enter cost ($ in millions)'}
                tooltip = { TOOLTIPS.architecture }
                thousandSeparator={true} 
                background={'rgba(0, 112, 192, 0.7)'}
                name="architecture"
                errorMsg={form.errors.architecture && 'required'}
                {...props}
              />    
            )}
          />
          <Controller 
            name="furniture"
            control={form.control}
            rules={{ required: true }}
            render={props => (
              <FormItem
                title={'Furniture, Fixtures and Equipment Cost'}
                placeholder={'Enter cost ($ in millions)'}
                tooltip = { TOOLTIPS.furniture }
                thousandSeparator={true} 
                background={'rgba(0, 112, 192, 0.7)'}
                name="furniture"
                errorMsg={form.errors.furniture && 'required'}
                {...props}
              />
            )}
          />
          <br/>
          <h3>3. Enter Operating Revenue</h3>
          <br/>
          <Controller 
            name="revenue"
            control={form.control}
            rules={{ required: true }}
            render={props => (                
              <FormItem
                title={'Projected Annual Revenue'}
                background="rgb(152, 135, 253)"
                placeholder={'Enter revenue ($ in millions)'}
                tooltip = { TOOLTIPS.revenue }
                thousandSeparator={true} 
                name="revenue"
                errorMsg={form.errors.revenue && 'required'}
                {...props}
              />
            )}
          />
          <BtnPrimary style={{width: '100%'}} onClick={ () => form.handleSubmit(submit) }>
            Calculate EB-5 Job Creation and Maximum EB-5 Raise
          </BtnPrimary>
        </form>
      </div>
    </div>
  );
}

export default Costing;
